import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import consentService from '../services/consent/consentService';
import { _applyConsent } from '../services/consent/loadScript';
import cleanupCookies from '../services/consent/cookieCleanup';
import { useRegion } from './RegionContext';

const ConsentContext = createContext({
    consent: null,
    policy: null,
    ready: false,
    needsBanner: false,
    isEU: false,
    isCalifornia: false,
    accept: async () => {},
    acceptAll: async () => {},
    rejectAll: async () => {},
    withdraw: async () => {},
    openPreferences: () => {},
    closePreferences: () => {},
    isPreferencesOpen: false,
});

export function ConsentProvider({ children }) {
    const { region } = useRegion();
    const [consent, setConsent] = useState(() => consentService.getCachedLocal());
    const [policy, setPolicy] = useState(null);
    const [ready, setReady] = useState(false);
    const [needsBanner, setNeedsBanner] = useState(false);
    const [isPreferencesOpen, setPreferencesOpen] = useState(false);
    const requestedFetch = useRef(false);

    // Apply cached consent to the loadScript gate ASAP — even before policy is fetched.
    useEffect(() => {
        if (consent?.categories) {
            _applyConsent(consent.categories);
        }
    }, [consent]);

    // Resolve real state from the server (also sets tx_aid cookie if missing).
    useEffect(() => {
        if (requestedFetch.current) return;
        requestedFetch.current = true;
        let cancelled = false;
        (async () => {
            try {
                const [meData, policyData] = await Promise.all([
                    consentService.fetchMe(),
                    consentService.fetchPolicy(),
                ]);
                if (cancelled) return;
                setPolicy(policyData);
                setConsent(meData);
                const showBanner = !!(meData?.firstTime || meData?.stale);
                setNeedsBanner(showBanner);
                if (meData?.categories) _applyConsent(meData.categories);
                setReady(true);
            } catch (e) {
                if (cancelled) return;
                // Network failure: fall back to whatever we cached locally. If nothing is
                // cached, show the banner with safe defaults rather than silently allowing.
                const cached = consentService.getCachedLocal();
                if (!cached) {
                    setNeedsBanner(true);
                }
                setReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const applyResult = useCallback((next) => {
        setConsent(next);
        setNeedsBanner(false);
        if (next?.categories) {
            _applyConsent(next.categories);
            cleanupCookies(next.categories);
        }
    }, []);

    const accept = useCallback(
        async (categories, source = 'preference_center') => {
            const next = await consentService.save({
                categories,
                source,
                policyVersion: policy?.version,
            });
            applyResult(next);
            return next;
        },
        [policy?.version, applyResult]
    );

    const acceptAll = useCallback(
        () => accept(consentService.acceptAll(), 'banner'),
        [accept]
    );

    const rejectAll = useCallback(
        () => accept(consentService.rejectAll(), 'banner'),
        [accept]
    );

    const withdraw = useCallback(async () => {
        const next = await consentService.withdraw();
        applyResult(next);
        return next;
    }, [applyResult]);

    const openPreferences = useCallback(() => setPreferencesOpen(true), []);
    const closePreferences = useCallback(() => setPreferencesOpen(false), []);

    const value = useMemo(
        () => ({
            consent,
            policy,
            ready,
            needsBanner,
            isEU: !!region?.isEU,
            isCalifornia: !!region?.isCalifornia,
            accept,
            acceptAll,
            rejectAll,
            withdraw,
            openPreferences,
            closePreferences,
            isPreferencesOpen,
        }),
        [
            consent,
            policy,
            ready,
            needsBanner,
            region,
            accept,
            acceptAll,
            rejectAll,
            withdraw,
            openPreferences,
            closePreferences,
            isPreferencesOpen,
        ]
    );

    return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

export function useConsent() {
    return useContext(ConsentContext);
}

/**
 * Render children only when the user has consented to the given category.
 *   <ConsentGate category="analytics"><AnalyticsWidget /></ConsentGate>
 */
export function ConsentGate({ category, fallback = null, children }) {
    const { consent, ready } = useConsent();
    if (!ready) return null;
    const allowed = category === 'necessary' || !!consent?.categories?.[category];
    return allowed ? children : fallback;
}

export default ConsentContext;
